import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, API, formatEUR, formatDateFR, todayISO, daysBetween } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { Plus, Search, Check, X, Trash2, Edit3, Paperclip, Download, Send } from "lucide-react";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const FILTERS = [
  { key: "tous", label: "Toutes" },
  { key: "en_retard", label: "En retard" },
  { key: "en_attente", label: "En attente" },
  { key: "payee", label: "Payées" },
];

export default function Factures() {
  const [factures, setFactures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const filter = params.get("statut") || "tous";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/factures");
      setFactures(data);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    let list = factures;
    if (filter !== "tous") list = list.filter((f) => f.statut === filter);
    if (q.trim()) {
      const lower = q.toLowerCase();
      list = list.filter(
        (f) =>
          f.numero.toLowerCase().includes(lower) ||
          (f.client_nom || "").toLowerCase().includes(lower) ||
          (f.description || "").toLowerCase().includes(lower)
      );
    }
    return list;
  }, [factures, filter, q]);

  const togglePaid = async (f) => {
    try {
      await api.patch(`/factures/${f.id}/toggle-paiement`);
      toast.success(
        !f.payee ? "Facture marquée comme payée" : "Facture repassée en attente"
      );
      load();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette facture ?")) return;
    try {
      await api.delete(`/factures/${id}`);
      toast.success("Facture supprimée");
      load();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const downloadPdf = async (f) => {
    try {
      const resp = await api.get(`/factures/${f.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `facture_${f.numero}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Erreur lors du téléchargement du PDF");
    }
  };

  const sendReminder = async (f) => {
    if (!window.confirm(`Envoyer un rappel à ${f.client_nom} pour la facture ${f.numero} ?`)) return;
    const t = toast.loading("Envoi du rappel...");
    try {
      const { data } = await api.post(`/factures/${f.id}/send-reminder`);
      toast.success(`Rappel envoyé à ${data.sent_to}`, { id: t });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'envoi", { id: t });
    }
  };

  return (
    <div data-testid="factures-page" className="space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">
            Factures
          </h1>
          <p className="text-[#5C665E] mt-2">
            {factures.length} facture{factures.length > 1 ? "s" : ""} au total
          </p>
        </div>
        <button
          data-testid="create-invoice-button"
          onClick={() => navigate("/factures/nouveau")}
          className="rounded-full px-5 py-2.5 text-sm font-semibold text-white btn-lift focus-brand flex items-center gap-2"
          style={{ backgroundColor: "#2C5545" }}
        >
          <Plus size={16} /> Nouvelle facture
        </button>
      </div>

      {/* Filtres + recherche */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1 rounded-full border p-1 bg-white" style={{ borderColor: "#E5E2DC" }}>
          {FILTERS.map((ft) => (
            <button
              key={ft.key}
              data-testid={`filter-${ft.key}`}
              onClick={() => {
                if (ft.key === "tous") setParams({});
                else setParams({ statut: ft.key });
              }}
              className={`px-3.5 py-1.5 text-xs font-semibold rounded-full transition-colors duration-200 ${
                filter === ft.key
                  ? "bg-[#2C5545] text-white"
                  : "text-[#5C665E] hover:text-[#1A1D1A]"
              }`}
            >
              {ft.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C665E]" />
          <Input
            data-testid="search-input"
            placeholder="Rechercher par numéro, client, description..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 bg-white border-[#E5E2DC] rounded-lg h-10"
          />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl border bg-white overflow-hidden" style={{ borderColor: "#E5E2DC" }}>
        {loading ? (
          <div className="p-8 text-[#5C665E] text-sm">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-[#5C665E] mb-4">Aucune facture ne correspond.</p>
            <button
              data-testid="empty-create-btn"
              onClick={() => navigate("/factures/nouveau")}
              className="rounded-full px-4 py-2 text-sm font-semibold text-white btn-lift"
              style={{ backgroundColor: "#2C5545" }}
            >
              Créer une facture
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-[#5C665E]" style={{ backgroundColor: "#F9F8F6" }}>
                  <th className="px-6 py-3 font-medium">Numéro</th>
                  <th className="px-6 py-3 font-medium">Client</th>
                  <th className="px-6 py-3 font-medium">Émission</th>
                  <th className="px-6 py-3 font-medium">Échéance</th>
                  <th className="px-6 py-3 font-medium text-right">Montant</th>
                  <th className="px-6 py-3 font-medium">Statut</th>
                  <th className="px-6 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((f) => {
                  const days = daysBetween(todayISO(), f.date_echeance);
                  return (
                    <tr key={f.id} className="row-hover border-t" style={{ borderColor: "#E5E2DC" }} data-testid={`facture-row-${f.id}`}>
                      <td className="px-6 py-4 font-mono-num font-medium">
                        <div className="flex items-center gap-2">
                          {f.numero}
                          {f.pdf_file_id && (
                            <a
                              href={`${API}/files/${f.pdf_file_id}`}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`pdf-link-${f.id}`}
                              title="Voir le PDF"
                              className="text-[#2C5545] hover:text-[#1E3B30]"
                            >
                              <Paperclip size={12} />
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">{f.client_nom}</td>
                      <td className="px-6 py-4 text-[#5C665E] font-mono-num text-xs">{formatDateFR(f.date_emission)}</td>
                      <td className="px-6 py-4 font-mono-num text-xs">
                        <div>{formatDateFR(f.date_echeance)}</div>
                        {f.statut === "en_retard" && (
                          <div className="text-[#B3261E] text-xs mt-0.5">Retard de {Math.abs(days)}j</div>
                        )}
                        {f.statut === "en_attente" && days >= 0 && (
                          <div className="text-[#5C665E] text-xs mt-0.5">Dans {days}j</div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right font-mono-num font-semibold">{formatEUR(f.montant)}</td>
                      <td className="px-6 py-4">
                        <StatusBadge status={f.statut} pulse={f.statut === "en_retard"} />
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button
                            data-testid={`toggle-paid-${f.id}`}
                            onClick={() => togglePaid(f)}
                            title={f.payee ? "Repasser en attente" : "Marquer payée"}
                            className={`p-1.5 rounded-lg btn-lift focus-brand ${
                              f.payee
                                ? "bg-[#FFF2D1] text-[#8C6100] hover:bg-[#FFE08A]"
                                : "bg-[#DFF2E4] text-[#1E6534] hover:bg-[#B4E3C1]"
                            }`}
                          >
                            {f.payee ? <X size={14} /> : <Check size={14} />}
                          </button>
                          <button
                            data-testid={`download-pdf-${f.id}`}
                            onClick={() => downloadPdf(f)}
                            title="Télécharger PDF"
                            className="p-1.5 rounded-lg text-[#2C5545] hover:bg-[#DFF2E4] btn-lift focus-brand"
                          >
                            <Download size={14} />
                          </button>
                          {!f.payee && (
                            <button
                              data-testid={`send-reminder-${f.id}`}
                              onClick={() => sendReminder(f)}
                              title="Envoyer un rappel au client"
                              className={`p-1.5 rounded-lg btn-lift focus-brand ${
                                f.statut === "en_retard"
                                  ? "bg-[#FCE8E8] text-[#B3261E] hover:bg-[#F8B4B4]"
                                  : "text-[#5C665E] hover:bg-[#F9F8F6]"
                              }`}
                            >
                              <Send size={14} />
                            </button>
                          )}
                          <button
                            data-testid={`edit-facture-${f.id}`}
                            onClick={() => navigate(`/factures/${f.id}/edit`)}
                            className="p-1.5 rounded-lg text-[#5C665E] hover:bg-[#F9F8F6] btn-lift focus-brand"
                            title="Modifier"
                          >
                            <Edit3 size={14} />
                          </button>
                          <button
                            data-testid={`delete-facture-${f.id}`}
                            onClick={() => remove(f.id)}
                            className="p-1.5 rounded-lg text-[#B3261E] hover:bg-[#FCE8E8] btn-lift focus-brand"
                            title="Supprimer"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
